import { forwardRef } from "react";
import { StyleSheet, Text, View, type TextInput } from "react-native";

import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { TextField } from "../primitives/TextField";
import type { UiTheme } from "../theme";

// PROF-5 — le sei caselle del codice di verifica.
//
// ⭐ È UN campo solo, dipinto come sei. Scelta di Antenore il 3/09, e non è
// estetica: le due scocciature che ha riportato dopo il suo reset si curano
// entrambe qui.
//   (b) I password manager riempivano il codice con lo USERNAME, perché un
//       campo di testo nudo senza `autoComplete` sembra proprio quello. La
//       cura è `one-time-code`, e Android, iOS e i browser la offrono a UN
//       campo — mai a sei da una cifra. Con sei input veri l'autofill del
//       codice dalla mail non arriva affatto.
//   E l'incolla, che lui ha chiesto esplicitamente, qui non si scrive: è
//   quello che un input fa già. Con sei campi andrebbe intercettato e
//   distribuito a mano.
//
// L'avanzamento del cursore che ha descritto è il CARET: si sposta da sé,
// perché c'è un solo testo. Le caselle disegnate leggono `value` e basta.
//
// ⛔ Le sei caselle sono DECORAZIONE e vanno nascoste all'assistente vocale:
// altrimenti chi non vede sente sei riquadri muti invece di un campo che si
// chiama «Codice di verifica». Il trio delle tre prop sta in
// `primitives/hiddenFromScreenReader` (PG-34: stesso bisogno all'hint del
// cursore del grafico).
export type CodeFieldProps = {
  theme: UiTheme;
  /** Quante caselle. Cognito ne manda sei; resta un parametro perché il numero
   *  è del mittente, non di questo componente. */
  length?: number;
  value: string;
  /** Riceve SOLO cifre, già tagliate a `length`. Un incolla sporco
   *  («123 456», «Code: 123456») arriva qui pulito. */
  onChangeText: (next: string) => void;
  accessibilityLabel: string;
  editable?: boolean;
  invalid?: boolean;
};

export const CodeField = forwardRef<TextInput, CodeFieldProps>(function CodeField(
  { theme, length = 6, value, onChangeText, accessibilityLabel, editable = true, invalid = false },
  ref,
) {
  const styles = makeStyles(theme);
  // La casella che sta per ricevere — e NESSUNA quando il codice è completo.
  // ⛔ Visto al banco, non deciso a tavolino: tenendo accesa l'ultima a codice
  // pieno, quella casella si legge «questa è diversa dalle altre» proprio nel
  // momento in cui la risposta giusta è «finito, premi pure».
  const activeIndex = value.length < length ? value.length : -1;

  return (
    <View style={styles.wrap}>
      <View style={styles.boxes} {...hiddenFromScreenReader()}>
        {Array.from({ length }, (_, i) => (
          <View
            key={i}
            style={[
              styles.box,
              invalid && styles.boxInvalid,
              !invalid && editable && i === activeIndex && styles.boxActive,
            ]}
          >
            <Text style={styles.digit}>{value[i] ?? ""}</Text>
          </View>
        ))}
      </View>

      {/* Il campo vero, sopra le caselle e trasparente. ⛔ Le tre neutralizzazioni
          sono volute, non una lotta col primitivo: qui la scatola NON è il campo,
          è il disegno sotto. Resta `TextField` perché porta l'`aria-invalid` del
          web e la cura Android del padding, che valgono anche a un campo che non
          si vede. `caretHidden`: il cursore vero cadrebbe fuori dalle caselle. */}
      <TextField
        ref={ref}
        theme={theme}
        accessibilityLabel={accessibilityLabel}
        autoCapitalize="none"
        autoComplete="one-time-code"
        autoCorrect={false}
        caretHidden
        editable={editable}
        inputMode="numeric"
        invalid={invalid}
        keyboardType="number-pad"
        maxLength={length}
        style={styles.input}
        textContentType="oneTimeCode"
        value={value}
        // Un incolla arriva qui in un colpo solo: si tengono le cifre e si
        // taglia. ⛔ Non si rifiuta l'input sporco — chi incolla «123 456» ha
        // fatto la cosa giusta, ed è il campo che deve capirlo.
        onChangeText={(text) =>
          onChangeText(text.replace(/\D/g, "").slice(0, length))
        }
      />
    </View>
  );
});

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { position: "relative" },
    boxes: { flexDirection: "row", gap: s.s2 },
    box: {
      flex: 1,
      // 48, non 44: il pavimento del tocco è il minimo, e qui il bersaglio è
      // un sesto di colonna — stretto in orizzontale, quindi si compensa in
      // altezza. Sotto font-scale cresce, perché è `minHeight`.
      minHeight: 48,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      // ⛔ `borderStrong` e non `border`: queste sei scatole SONO il controllo, e
      // chi guarda deve contarle prima di digitare. Visto al banco nel tema
      // scuro, dove con `border` le caselle vuote sparivano quasi nel fondo.
      // È esattamente il caso per cui il token esiste (DS-9 + DS-23: gatato
      // >=3:1 contro `surface` e `surface2` in tutte e sei le famiglie).
      borderColor: p.borderStrong,
      borderRadius: r.r3,
      backgroundColor: p.surface,
    },
    // ⛔ Il bordo più marcato NON è l'unico segno che la casella è quella
    // attiva: è la prima vuota dopo le piene, cioè la posizione la dice già la
    // sequenza. Il colore rinforza, non informa da solo.
    boxActive: { borderWidth: 2, borderColor: p.brandText },
    boxInvalid: { borderColor: p.danger },
    digit: {
      color: p.ink,
      fontSize: ty.sizes.xl,
      fontFamily: ty.fonts.mono,
      // Le cifre non devono ballare mentre si digita.
      fontVariant: ["tabular-nums"],
    },
    input: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      borderWidth: 0,
      backgroundColor: "transparent",
      // ⚠️ Trasparente e non `opacity: 0`: il browser ancora il menu
      // dell'autofill alla scatola del campo, e un campo a opacità zero è
      // proprio quello che alcuni gestori di password saltano — cioè l'esatto
      // contrario di quel che questo file esiste per ottenere.
      color: "transparent",
      textAlign: "center",
    },
  });
}
